import React, { useMemo, useRef, useState } from "react";
import * as Icons from "lucide-react";
import { Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { PageHeader, SectionCard, StatusBadge, ProvenanceBadge, SignatureBadge, Mono, InfoNote } from "@/components/uikit";
import { PAYLOAD_TYPES } from "@/lib/constants";
import { isValidReceipt } from "@/lib/crypto";
import { downloadJson } from "@/lib/download";
import { toast } from "sonner";

export default function Vault() {
  const { records, integrity, sigStatus, identity, signRecord, deleteRecord, clearAll, reseed, importRecords } = useApp();
  const [q, setQ] = useState("");
  const [typeF, setTypeF] = useState("all");
  const [integrityF, setIntegrityF] = useState("all");
  const fileRef = useRef(null);

  const rows = useMemo(() => {
    return records
      .map((r, i) => ({ r, i, ok: integrity[i] ? integrity[i].selfOk && integrity[i].linkOk : true }))
      .filter(({ r, ok }) => {
        if (typeF !== "all" && r.payload_type !== typeF) return false;
        if (integrityF === "verified" && !ok) return false;
        if (integrityF === "failed" && ok) return false;
        if (q) {
          const hay = `${r.event_id} ${r.payload_type} ${r.sha256} ${r.notes} ${r.payload}`.toLowerCase();
          if (!hay.includes(q.toLowerCase())) return false;
        }
        return true;
      })
      .reverse();
  }, [records, integrity, q, typeF, integrityF]);

  const importFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      let data;
      try {
        data = JSON.parse(String(reader.result || ""));
      } catch {
        toast.error("Invalid JSON file", { description: "The file could not be parsed as JSON." });
        return;
      }
      const list = Array.isArray(data) ? data : [data];
      const valid = list.filter(isValidReceipt);
      const skipped = list.length - valid.length;
      if (valid.length === 0) {
        toast.error("No valid receipts found", {
          description: "Records must include a 64-char sha256, event_id, timestamp and payload.",
        });
        return;
      }
      importRecords(valid);
      toast.success(`Imported ${valid.length} record${valid.length === 1 ? "" : "s"}`, {
        description: skipped > 0 ? `${skipped} malformed record${skipped === 1 ? "" : "s"} skipped.` : undefined,
      });
    };
    reader.onerror = () => toast.error("Could not read file");
    reader.readAsText(file);
    e.target.value = "";
  };

  return (
    <div>
      <PageHeader overline="Local Persistent Storage" title="Evidence Vault" icon="Database"
        subtitle="All records persist in browser-local storage and survive navigation and refresh."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline" className="border-cyan-800 text-cyan-300 hover:bg-cyan-950/40">
              <Link to="/evidence-pack" data-testid="vault-evidence-pack-link"><Icons.FileText className="mr-2 h-4 w-4" /> Evidence Pack</Link>
            </Button>
            <Button variant="outline" className="border-slate-700 text-slate-200" onClick={() => downloadJson(records, "signallink-vault-export.json")} data-testid="vault-export-all">
              <Icons.Download className="mr-2 h-4 w-4" /> Export all
            </Button>
            <Button variant="outline" className="border-slate-700 text-slate-200" onClick={() => fileRef.current?.click()} data-testid="vault-import">
              <Icons.Upload className="mr-2 h-4 w-4" /> Import
            </Button>
            <input ref={fileRef} type="file" accept="application/json,.json" className="hidden" onChange={importFile} data-testid="vault-file-input" />
          </div>
        } />

      <SectionCard title="Filter & Search" icon="Filter" className="mb-6" testId="vault-filters">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="relative">
            <Icons.Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" aria-hidden="true" />
            <Input data-testid="vault-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search events, hashes, notes…"
              className="border-slate-700 bg-slate-950 pl-9 text-slate-200" />
          </div>
          <Select value={typeF} onValueChange={setTypeF}>
            <SelectTrigger data-testid="vault-type-filter" className="border-slate-700 bg-slate-950 text-slate-200"><SelectValue /></SelectTrigger>
            <SelectContent className="border-slate-700 bg-slate-900 text-slate-200">
              <SelectItem value="all">All types</SelectItem>
              {PAYLOAD_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={integrityF} onValueChange={setIntegrityF}>
            <SelectTrigger data-testid="vault-integrity-filter" className="border-slate-700 bg-slate-950 text-slate-200"><SelectValue /></SelectTrigger>
            <SelectContent className="border-slate-700 bg-slate-900 text-slate-200">
              <SelectItem value="all">All integrity</SelectItem>
              <SelectItem value="verified">Verified only</SelectItem>
              <SelectItem value="failed">Failures only</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </SectionCard>

      <SectionCard title={`Records (${rows.length})`} icon="Database" testId="vault-table-card"
        right={
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="h-8 border-slate-700 text-slate-300" onClick={() => { reseed(); toast.success("Demo data restored"); }} data-testid="vault-reseed">
              <Icons.RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Reseed demo
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="outline" className="h-8 border-rose-800 text-rose-300 hover:bg-rose-950/40" data-testid="vault-clear">
                  <Icons.Trash2 className="mr-1.5 h-3.5 w-3.5" /> Clear all
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="border-slate-700 bg-[#0D121D] text-slate-200">
                <AlertDialogHeader>
                  <AlertDialogTitle>Clear the entire evidence vault?</AlertDialogTitle>
                  <AlertDialogDescription className="text-slate-400">This permanently deletes all local records. This cannot be undone.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="border-slate-700 bg-transparent text-slate-300">Cancel</AlertDialogCancel>
                  <AlertDialogAction className="bg-rose-600 text-white hover:bg-rose-500" onClick={() => { clearAll(); toast.success("Vault cleared"); }} data-testid="vault-clear-confirm">Clear vault</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        }>
        {rows.length === 0 ? (
          <div className="py-10 text-center">
            <Icons.Database className="mx-auto mb-3 h-10 w-10 text-slate-700" aria-hidden="true" />
            <p className="text-sm text-slate-500">No records match. Adjust filters or reseed demo data.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-800 font-mono text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="py-2 pr-3">#</th>
                  <th className="py-2 pr-3">Event / Type</th>
                  <th className="py-2 pr-3">Timestamp</th>
                  <th className="py-2 pr-3">Integrity</th>
                  <th className="py-2 pr-3">Signature</th>
                  <th className="py-2 pr-3">Source</th>
                  <th className="py-2 pr-3">SHA-256</th>
                  <th className="py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ r, i, ok }) => (
                  <tr key={r.id} className="border-b border-slate-900 text-slate-300" data-testid={`vault-row-${r.id}`}>
                    <td className="py-2.5 pr-3 font-mono text-slate-500">{i}</td>
                    <td className="py-2.5 pr-3">
                      <Mono>{r.event_id}</Mono>
                      <p className="mt-0.5 text-xs text-slate-500">{r.payload_type}</p>
                    </td>
                    <td className="py-2.5 pr-3 text-xs text-slate-400">{new Date(r.timestamp).toLocaleString()}</td>
                    <td className="py-2.5 pr-3"><StatusBadge status={ok ? "VERIFIED" : "WARNING"} /></td>
                    <td className="py-2.5 pr-3"><SignatureBadge state={sigStatus[r.id] || "UNSIGNED RECEIPT"} /></td>
                    <td className="py-2.5 pr-3"><ProvenanceBadge source={r.source_tag} /></td>
                    <td className="py-2.5 pr-3"><Mono className="inline-block max-w-[120px] truncate align-middle">{r.sha256}</Mono></td>
                    <td className="py-2.5">
                      <div className="flex justify-end gap-1">
                        {identity && (
                          <button className="rounded p-1.5 text-slate-500 hover:bg-slate-800 hover:text-emerald-400" aria-label="Sign record"
                            onClick={async () => { await signRecord(r.id); toast.success("Record signed", { description: r.event_id }); }} data-testid={`vault-sign-${r.id}`}>
                            <Icons.PenTool className="h-4 w-4" />
                          </button>
                        )}
                        <button className="rounded p-1.5 text-slate-500 hover:bg-slate-800 hover:text-cyan-400" aria-label="Download receipt"
                          onClick={() => downloadJson(r, `signallink-receipt-${r.event_id}.json`)} data-testid={`vault-download-${r.id}`}>
                          <Icons.Download className="h-4 w-4" />
                        </button>
                        <button className="rounded p-1.5 text-slate-500 hover:bg-slate-800 hover:text-rose-400" aria-label="Delete record"
                          onClick={() => { deleteRecord(r.id); toast.success("Record deleted"); }} data-testid={`vault-delete-${r.id}`}>
                          <Icons.Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <InfoNote tone="info" className="mt-4">
          Deleting or importing records changes chain continuity. Use <strong>Reconstruction</strong> to inspect any
          resulting break.
        </InfoNote>
      </SectionCard>
    </div>
  );
}
